/**
 * @file domain-columns.tsx
 * @description Колонки и действия строки списка адресов магазина
 * (phase19.28 этап А; uiux-platform-shops §5): «Адрес | Роль | Статус |
 * DNS | Прокси | HTTPS | Проверено» в таблице, на телефоне — строка-карточка
 * с теми же данными и «⋯».
 *
 * Действия — реестр по правилам `domainActionRules`: недоступное действие
 * остаётся в меню неактивным с причиной («Адрес ещё не работает…»), а не
 * исчезает молча. Длинный адрес переносится по символам, полный — в
 * подсказке; у IDN — Юникод и мелко punycode.
 */

import { BookOpen, RefreshCw, Star, Trash2, Power } from "lucide-react";

import type { ShopDomain } from "../../../lib/api-platform-shops";
import { checkStatusDetail, domainActionRules, domainRoleLabel, domainStatusMeta } from "../../../lib/platform/shop-domain-status";
import { formatAgo } from "../../../lib/platform/shop-format";
import type { ListColumn } from "../../ui/responsive-list-types";
import type { RowAction } from "../../ui/RowActionsMenu";
import { StatusPill } from "../../ui/StatusPill";
import { DomainCheckMark, DomainCheckMarks } from "./DomainCheckMarks";
import { DomainHostCell } from "./DomainHostCell";

const checkedLabel = (d: ShopDomain) => (d.lastCheckAt ? `проверено ${formatAgo(d.lastCheckAt)}` : "не проверялся");

/**
 * @param expectedIps IP сервера платформы (edge-config) — в подсказке «DNS указывает на X, нужен Y»;
 *   в кабинете магазина их нет — пустой список.
 */
export function domainColumns(primaryHost: string | null, checkingId: string | null, expectedIps: readonly string[] = []): ListColumn<ShopDomain>[] {
  // Статус: пилюля переносится (длинное «DNS не на сервер: …» не наезжает на соседей), под ней — текст сервера.
  const statusCell = (d: ShopDomain) => {
    const detail = d.status === "ACTIVE" && checkingId !== d.id ? checkStatusDetail(d.lastCheck) : null;
    return (
      <span className="flex min-w-0 max-w-full flex-col items-start gap-0.5">
        <StatusPill meta={domainStatusMeta(d, checkingId === d.id)} className="h-auto max-w-full whitespace-normal break-words text-left" />
        {detail ? <span className="type-caption break-words text-on-surface-variant">{detail}</span> : null}
      </span>
    );
  };
  return [
    // Адрес — забирает остаток ширины (не уже ~200px при фиксированных соседях, QA D-N1).
    { key: "host", header: "Адрес", compact: "title", cell: (d) => <DomainHostCell domain={d} /> },
    {
      key: "role",
      header: "Роль",
      width: "w-[160px]",
      compact: "meta",
      cell: (d) => domainRoleLabel(d, primaryHost),
      // 1024–1279: роль — второй линией адреса, адресу не тесно.
      fold: { into: "host" },
    },
    // На телефоне статус — своей строкой под адресом, а не справа от него (адрес не сжимается в 0).
    { key: "status", header: "Статус", width: "w-[200px]", compact: "subtitle", cell: statusCell },
    { key: "dns", header: "DNS", width: "w-[64px]", cell: (d) => <DomainCheckMark check={d.lastCheck} part="dns" expectedIps={expectedIps} /> },
    { key: "proxy", header: "Прокси", width: "w-[72px]", cell: (d) => <DomainCheckMark check={d.lastCheck} part="proxy" /> },
    { key: "cert", header: "HTTPS", width: "w-[96px]", cell: (d) => <DomainCheckMark check={d.lastCheck} part="cert" /> },
    { key: "marks", header: "Проверки", compact: "meta", compactOnly: true, cell: (d) => <DomainCheckMarks check={d.lastCheck} expectedIps={expectedIps} /> },
    {
      key: "checked",
      header: "Проверено",
      width: "w-[112px]",
      compact: "meta",
      cell: (d) => <span className="type-caption text-on-surface-variant">{checkedLabel(d)}</span>,
    },
  ];
}

export interface DomainHandlers {
  recheck: (d: ShopDomain) => void;
  guide: (d: ShopDomain) => void;
  makePrimary: (d: ShopDomain) => void;
  toggle: (d: ShopDomain) => void;
  remove: (d: ShopDomain) => void;
}

/** Пункты «⋯» адреса: доступность и причина — из правил, опасное — последним. */
export function domainRowActions(domain: ShopDomain, handlers: DomainHandlers, checking: boolean): RowAction[] {
  const rules = domainActionRules(domain);
  const actions: RowAction[] = [
    { key: "recheck", label: "Проверить", icon: RefreshCw, disabled: checking, run: () => handlers.recheck(domain) },
    { key: "guide", label: "Инструкция", icon: BookOpen, run: () => handlers.guide(domain) },
  ];
  if (!domain.isPrimary) {
    actions.push({
      key: "primary",
      label: "Сделать главным",
      icon: Star,
      disabled: !rules.makePrimary.allowed,
      hint: rules.makePrimary.reason,
      run: () => handlers.makePrimary(domain),
    });
  }
  if (rules.toggle.allowed) {
    actions.push({
      key: "toggle",
      label: domain.status === "ACTIVE" ? "Отключить" : "Включить",
      icon: Power,
      danger: domain.status === "ACTIVE",
      run: () => handlers.toggle(domain),
    });
  }
  if (domain.kind === "CUSTOM") {
    actions.push({
      key: "remove",
      label: "Удалить",
      icon: Trash2,
      danger: true,
      disabled: !rules.remove.allowed,
      hint: rules.remove.reason,
      run: () => handlers.remove(domain),
    });
  }
  return actions;
}
